// DashboardBuilder.js
import React, { useState, useMemo, useContext, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { saveAs } from "file-saver";
import VariableContext from "../contexts/VariableContext";

import Sidebar from "../components/DashboardBuilder/Sidebar";
import TabNavigation from "../components/DashboardBuilder/TabNavigation";
import TabContent from "../components/DashboardBuilder/TabContent";
import GlobalFilters from "../components/DashboardBuilder/GlobalFilters";
import "./styles/DashboardBuilder.css";
import generateChartData from "../utils/generateChartData";
import generateCrosstab from "../utils/generateCrosstab";
import config from "../configs/config";
import MultiResponse from "./MultiResponse";
import FetchingFreq from "../contexts/FetchingFreq";

const DashboardBuilder = () => {
  const { dataGroup, originalDataRows, projectId } =
    useContext(VariableContext);
  const { frequencyData } = useContext(FetchingFreq);

  const [tabs, setTabs] = useState([
    { id: uuidv4(), title: "Overview", filters: [], kpis: [] },
  ]);
  const [dataDashboard, setDataDashboard] = useState(null);
  const [selectedTabId, setSelectedTabId] = useState(tabs[0].id);
  const [globalFilters, setGlobalFilters] = useState([]);
  const [selectedQuestionKey, setSelectedQuestionKey] = useState(null);
  const [logoPath, setLogoPath] = useState("");
  const [showCrosstabModal, setShowCrosstabModal] = useState(false);
  const [crosstabGenerated, setCrosstabGenerated] = useState(false);
  const [crosstabDisplayData, setCrosstabDisplayData] = useState(null);

  const selectedTab = useMemo(
    () => tabs.find((tab) => tab.id === selectedTabId),
    [tabs, selectedTabId]
  );

  const selectedVariable = useMemo(
    () =>
      selectedQuestionKey && dataGroup ? dataGroup[selectedQuestionKey] : null,
    [selectedQuestionKey, dataGroup]
  );

  // 🔥 NEW: Recalculate all KPI data when filters change
  useEffect(() => {
    if (!selectedTab || !originalDataRows || originalDataRows.length === 0)
      return;

    const currentTab = tabs.find((t) => t.id === selectedTabId);
    if (!currentTab) return;

    const filteredRows = applyAllFilters(
      originalDataRows,
      currentTab,
      globalFilters
    );
    const totalBase = originalDataRows.length; // Keep original base for percentages

    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== selectedTabId) return tab;

        const updatedKPIs = tab.kpis.map((kpi) => {
          // Recalculate crosstab data
          if (kpi.isCrosstab) {
            const rowKeys = kpi.rowVars || [];
            const colKeys = kpi.colVars || [];

            if (!rowKeys.length || !colKeys.length) return kpi;

            const rowMetas = rowKeys.map((k) => dataGroup[k] || {});
            const colMetas = colKeys.map((k) => dataGroup[k] || {});

            const newData = generateCrosstab(
              filteredRows, // ✅ FILTERED rows
              rowKeys, // ✅ ARRAY (nested supported)
              colKeys,
              rowMetas,
              colMetas
            );

            if (newData) {
              return { ...kpi, data: newData };
            }
          }

          // Recalculate regular KPI data
          else if (Array.isArray(kpi.variableName)) {
            // Multi-variable KPI - recalculate combined data
            const variableKeys = kpi.variableName;
            const types = variableKeys.map((key) => {
              const v = dataGroup[key];
              if (!v) return null;
              return detectVariableType(v, key, filteredRows);
            });

            const varType = types[0];
            let combinedData = [];

            if (varType === "multigrid") {
              variableKeys.forEach((key) => {
                const selectedVar = dataGroup[key];
                const variableKey = resolveVariableKey(selectedVar, key);

                const allCols = Object.keys(filteredRows[0] || {});
                const prefixMatch = variableKey?.match(/^([A-Za-z]+\d+)/);
                const prefix = prefixMatch ? prefixMatch[1] : variableKey;
                const gridCols = allCols.filter((c) =>
                  c.startsWith(prefix + "r")
                );

                const rowMap = {};
                if (Array.isArray(selectedVar?.Rows)) {
                  selectedVar.Rows.forEach((r) => {
                    const colNameMatch = r?.title?.match(
                      /^([A-Za-z0-9_]+r\d+)(.*)$/
                    );
                    if (colNameMatch) {
                      const colName = colNameMatch[1];
                      const label = colNameMatch[2].trim();
                      rowMap[colName] = label || colName;
                    }
                  });
                }

                // Use original rows for base calculation
                const overallBase = originalDataRows.filter((row) =>
                  gridCols.some(
                    (col) =>
                      row[col] !== null &&
                      row[col] !== undefined &&
                      row[col] !== ""
                  )
                ).length;

                gridCols.forEach((col) => {
                  let count = 0;
                  filteredRows.forEach((row) => {
                    if (row[col] == 1) count++;
                  });

                  const label = rowMap[col] || col;

                  combinedData.push({
                    name: label,
                    value: count,
                    percent: overallBase
                      ? Number(((count / overallBase) * 100).toFixed(2))
                      : 0,
                    base: overallBase,
                    variable: key,
                  });
                });
              });
            } else if (varType === "categorical") {
              variableKeys.forEach((key) => {
                const selectedVar = dataGroup[key];
                const variableKey = resolveVariableKey(selectedVar, key);

                let options = [];
                if (
                  Array.isArray(selectedVar?.Rows) &&
                  selectedVar.Rows.length
                ) {
                  options = selectedVar.Rows.map((r) => {
                    const m = r?.title?.match(/^([A-Za-z0-9_]+r\d+)(.*)$/);
                    const value = r.index ?? r.value ?? r.Id ?? r.index;
                    const label = m ? m[2].trim() : r?.title;
                    return { value: String(value), label };
                  });
                } else if (
                  Array.isArray(selectedVar?.values) &&
                  selectedVar.values.length
                ) {
                  options = selectedVar.values.map((v) => ({
                    value: String(v.index ?? v.Value ?? v.value),
                    label: v?.title || v.label || String(v.index),
                  }));
                }

                // Generate data with original base
                const originalData = generateChartData(
                  originalDataRows,
                  variableKey,
                  { values: options }
                );
                const originalBase = originalData[0]?.base || totalBase;

                // Count from filtered rows
                const filteredData = generateChartData(
                  filteredRows,
                  variableKey,
                  { values: options }
                );

                // Combine: filtered counts with original base
                filteredData.forEach((item, idx) => {
                  combinedData.push({
                    name: item.name,
                    value: item.value, // Count from filtered rows
                    percent: originalBase
                      ? Number(((item.value / originalBase) * 100).toFixed(2))
                      : 0, // Percentage against original base
                    base: originalBase, // Original base
                    variable: key,
                  });
                });
              });
            } else if (varType === "numeric") {
              variableKeys.forEach((key) => {
                const selectedVar = dataGroup[key];
                const variableKey = resolveVariableKey(selectedVar, key);

                const nums = filteredRows
                  .map((r) => Number(r[variableKey]))
                  .filter((n) => Number.isFinite(n));

                const base = nums.length;
                const mean = base ? nums.reduce((a, b) => a + b, 0) / base : 0;
                const variance = base
                  ? nums.reduce((acc, n) => acc + Math.pow(n - mean, 2), 0) /
                    base
                  : 0;
                const stdDev = Math.sqrt(variance);

                combinedData.push({
                  name: "Summary",
                  count: nums.length,
                  mean: Number(mean.toFixed(2)),
                  stdDev: Number(stdDev.toFixed(2)),
                  base,
                  stackKey: key,
                });
              });
            }

            return { ...kpi, data: combinedData };
          }
          // Single variable KPI
          else {
            const variableKey = resolveVariableKey(
              dataGroup[kpi.variableName],
              kpi.variableName
            );
            if (!variableKey) return kpi;

            const selectedVar = dataGroup[kpi.variableName];
            let options = kpi.options || [];

            if (options.length === 0 && selectedVar?.values) {
              options = selectedVar.values.map((v) => ({
                value: String(v.index ?? v.Value ?? v.value),
                label: v?.title || v.label || String(v.index),
              }));
            }

            // Get original base
            const originalData = generateChartData(
              originalDataRows,
              variableKey,
              { values: options }
            );
            const originalBase = originalData[0]?.base || totalBase;

            // Get filtered counts
            const filteredData = generateChartData(filteredRows, variableKey, {
              values: options,
            });

            // Recalculate with original base
            const newData = filteredData.map((item) => ({
              ...item,
              percent: originalBase
                ? Number(((item.value / originalBase) * 100).toFixed(2))
                : 0,
              base: originalBase,
            }));

            return { ...kpi, data: newData };
          }

          return kpi;
        });

        return { ...tab, kpis: updatedKPIs };
      })
    );
  }, [selectedTabId, globalFilters, dataGroup, originalDataRows]);

  function resolveRowCols(variableMeta) {
    if (!variableMeta) return [];
    if (
      variableMeta.type === "SingleResponse" &&
      Array.isArray(variableMeta.values)
    ) {
      return variableMeta.values.map((v) => v.index);
    }
    return variableMeta.varName || [];
  }

  const handleGenerateCrosstab = (rowVarsTree, colVarsTree) => {
    const filteredRows = applyAllFilters(
      originalDataRows,
      selectedTab,
      globalFilters
    );

    // Helper to flatten tree structure to get all variable IDs (original)
    const flattenTree = (tree) => {
      const result = [];
      const flatten = (nodes) => {
        nodes.forEach((node) => {
          result.push(node.id);
          if (node.children && node.children.length > 0) {
            flatten(node.children);
          }
        });
      };
      flatten(tree || []);
      return result;
    };

    // NEW: derive ordered list of variable keys from a tree (pre-order, parent before children)
    // This returns unique ids in preorder; if you structured the tree as variables nested, e.g. hidS2->S3,
    // it returns ['hidS2','S3'] which is what generateCrosstab expects for nested keys.
    const extractKeysForGenerate = (tree) => {
      if (!tree || tree.length === 0) return [];
      const res = [];
      const visit = (nodes) => {
        nodes.forEach((n) => {
          if (!res.includes(n.id)) res.push(n.id);
          if (n.children && n.children.length > 0) visit(n.children);
        });
      };
      visit(tree);
      return res;
    };

    // If the user passed a nested variable (var > var), we want that nesting preserved.
    // Use extractKeysForGenerate to produce arrays of keys for generateCrosstab.
    const rowKeysForGen = extractKeysForGenerate(rowVarsTree);
    const colKeysForGen = extractKeysForGenerate(colVarsTree);

    // Fallback: if extraction gives empty, fall back to flattenTree (older behavior)
    const rowKeysFinal =
      rowKeysForGen.length > 0 ? rowKeysForGen : flattenTree(rowVarsTree);
    const colKeysFinal =
      colKeysForGen.length > 0 ? colKeysForGen : flattenTree(colVarsTree);

    // console.log ("Row Variables (flatten):", flattenTree(rowVarsTree));
    // console.log ("Col Variables (flatten):", flattenTree(colVarsTree));
    // console.log ("Row Keys for generateCrosstab (ordered, nested-aware):", rowKeysFinal);
    // console.log ("Col Keys for generateCrosstab (ordered, nested-aware):", colKeysFinal);

    // Build metadata arrays aligned with keys (generateCrosstab accepts meta arrays)
    const rowMetasForGen = rowKeysFinal.map((k) => dataGroup[k] || {});
    const colMetasForGen = colKeysFinal.map((k) => dataGroup[k] || {});

    // Validate keys (null or missing metadata)
    const missingRow = rowKeysFinal.find(
      (k, i) => !k || rowMetasForGen[i] === undefined
    );
    const missingCol = colKeysFinal.find(
      (k, i) => !k || colMetasForGen[i] === undefined
    );

    if (missingRow || missingCol) {
      console.warn("Missing metadata or key for:", { missingRow, missingCol });
      // continue anyway — generateCrosstab is robust and will accept empty meta objects
    }

    // Call generateCrosstab with arrays for nested handling.
    // If single-level only, generateCrosstab will behave exactly as before.
    const result = generateCrosstab(
      filteredRows,
      rowKeysFinal, // can be array for nested keys or single-element array
      colKeysFinal,
      rowMetasForGen, // array of metas (aligned by index)
      colMetasForGen
    );

    if (!result || result.error) {
      alert(`Unable to generate crosstab: ${result?.error || "Unknown error"}`);
      return;
    }

    // Build KPI and store original tree for rendering & expand/collapse
    const newKPI = {
      id: uuidv4(),
      qTitle: `${rowKeysFinal.join(" × ")} x ${colKeysFinal.join(" × ")}`,
      label: `Crosstab: ${rowKeysFinal.join(", ")} x ${colKeysFinal.join(
        ", "
      )}`,
      type: "stackedcolumn",
      rowVars: flattenTree(rowVarsTree),
      colVars: flattenTree(colVarsTree),
      rowVarsTree, // original tree structure for UI
      colVarsTree, // original tree structure for UI
      isCrosstab: true,
      data: result,
      viewMode: "chart",
    };

    setTabs((prev) =>
      prev.map((tab) =>
        tab.id === selectedTabId ? { ...tab, kpis: [...tab.kpis, newKPI] } : tab
      )
    );

    setCrosstabGenerated(true);
    setShowCrosstabModal(false);
  };

  const resolveVariableKey = (
    selectedVariable,
    questionKey,
    fallbackVariableName
  ) => {
    if (!selectedVariable && !questionKey && !fallbackVariableName) return null;
    if (selectedVariable?.originalColumnName)
      return selectedVariable.originalColumnName;
    if (selectedVariable?.varName?.[0]?.Name)
      return selectedVariable.varName[0].Name;
    if (selectedVariable?.variableName) return selectedVariable.variableName;
    if (fallbackVariableName) return fallbackVariableName;
    if (questionKey) return questionKey;
    return null;
  };

  const isNumericVariable = (variable) => {
    if (!variable) return false;
    const t = (variable.questionType || variable.type || variable.Qtype || "")
      .toString()
      .toLowerCase();
    return ["numeric", "number", "integer", "float"].includes(t);
  };

  const computeKPIData = (
    variableKey,
    options = [],
    rows = originalDataRows
  ) => {
    const sourceRows = Array.isArray(rows) ? rows : [];
    const countsMap = new Map();
    let totalNonMissing = 0;

    sourceRows.forEach((row) => {
      let raw = row ? row[variableKey] : undefined;

      if (raw === undefined || raw === null || Number.isNaN(raw)) {
        raw = "__MISSING__";
      } else {
        if (typeof raw !== "string") raw = String(raw);
      }

      countsMap.set(raw, (countsMap.get(raw) || 0) + 1);
      if (raw !== "__MISSING__") totalNonMissing++;
    });

    let orderedKeys = [];
    if (options && options.length > 0) {
      const optionKeys = options.map((opt) => {
        if (typeof opt === "string") return opt;
        if (typeof opt === "object" && opt !== null) {
          return (
            opt.Value ?? opt.value ?? opt.Name ?? opt?.title ?? String(opt)
          );
        }
        return String(opt);
      });

      orderedKeys = [...optionKeys];
      countsMap.forEach((_, k) => {
        if (!orderedKeys.includes(k) && k !== "__MISSING__")
          orderedKeys.push(k);
      });
    } else {
      orderedKeys = Array.from(countsMap.keys()).filter(
        (k) => k !== "__MISSING__"
      );
    }

    const result = orderedKeys.map((k) => {
      const count = countsMap.get(k) || 0;
      const percent = totalNonMissing > 0 ? (count / totalNonMissing) * 100 : 0;
      return {
        name: k === "__MISSING__" ? "Missing" : k,
        value: count,
        percent: Math.round(percent * 100) / 100,
        base: totalNonMissing,
      };
    });

    if (countsMap.has("__MISSING__")) {
      const mcount = countsMap.get("__MISSING__") || 0;
      result.push({
        name: "Missing",
        value: mcount,
        percent: 0,
        base: totalNonMissing,
      });
    }

    return result;
  };

  const detectVariableType = (variable, key, rows) => {
    const variableKey = resolveVariableKey(variable, key);

    if (isNumericVariable(variable)) return "numeric";

    const allCols = Object.keys(rows?.[0] || {});
    const prefixMatch = variableKey?.match(/^([A-Za-z]+\d+)/);
    const prefix = prefixMatch ? prefixMatch[1] : variableKey;
    const gridCols = allCols.filter((c) => c.startsWith(prefix + "r"));
    if (gridCols.length > 1) return "multigrid";

    return "categorical";
  };

  const handleAddKPI = (variableKeys) => {
    if (!Array.isArray(variableKeys)) variableKeys = [variableKeys];
    if (variableKeys.length === 0) {
      alert("Select at least one variable.");
      return;
    }

    const types = variableKeys.map((key) => {
      const v = dataGroup[key];
      if (!v) return null;
      return detectVariableType(v, key, originalDataRows);
    });

    const uniqueTypes = [...new Set(types)];

    if (uniqueTypes.length > 1) {
      alert("You can only merge variables of the same type.");
      return;
    }

    const varType = uniqueTypes[0];
    const filteredRows = applyAllFilters(
      originalDataRows,
      selectedTab,
      globalFilters
    );
    const totalBase = originalDataRows.length; // Keep original base

    let combinedData = [];
    let combinedOptions = [];

    if (varType === "multigrid") {
      variableKeys.forEach((key) => {
        const selectedVar = dataGroup[key];
        const variableKey = resolveVariableKey(selectedVar, key);

        const allCols = Object.keys(originalDataRows[0] || {});
        const prefixMatch = variableKey?.match(/^([A-Za-z]+\d+)/);
        const prefix = prefixMatch ? prefixMatch[1] : variableKey;
        const gridCols = allCols.filter((c) => c.startsWith(prefix + "r"));

        const rowMap = {};
        if (Array.isArray(selectedVar?.Rows)) {
          selectedVar.Rows.forEach((r) => {
            const colNameMatch = r?.title?.match(/^([A-Za-z0-9_]+r\d+)(.*)$/);
            if (colNameMatch) {
              const colName = colNameMatch[1];
              const label = colNameMatch[2].trim();
              rowMap[colName] = label || colName;
            }
          });
        }

        // Use original rows for base
        const overallBase = originalDataRows.filter((row) =>
          gridCols.some(
            (col) =>
              row[col] !== null && row[col] !== undefined && row[col] !== ""
          )
        ).length;

        gridCols.forEach((col) => {
          let count = 0;
          filteredRows.forEach((row) => {
            if (row[col] == 1) count++;
          });

          const label = rowMap[col] || col;

          combinedData.push({
            name: label,
            value: count,
            percent: overallBase
              ? Number(((count / overallBase) * 100).toFixed(2))
              : 0,
            base: overallBase,
            variable: key,
          });

          combinedOptions.push({ variable: col, label });
        });
      });
    } else if (varType === "categorical") {
      variableKeys.forEach((key) => {
        const selectedVar = dataGroup[key];
        const variableKey = resolveVariableKey(selectedVar, key);

        let options = [];

        if (Array.isArray(selectedVar?.Rows) && selectedVar.Rows.length) {
          options = selectedVar.Rows.map((r) => {
            const m = r?.title?.match(/^([A-Za-z0-9_]+r\d+)(.*)$/);
            const value = r.index ?? r.value ?? r.Id ?? r.index;
            const label = m ? m[2].trim() : r?.title;
            return { value: String(value), label };
          });
        } else if (
          Array.isArray(selectedVar?.values) &&
          selectedVar.values.length
        ) {
          options = selectedVar.values.map((v) => ({
            value: String(v.index ?? v.Value ?? v.value),
            label: v?.title || v.label || String(v.index),
          }));
        }

        // Get original base
        const originalData = generateChartData(originalDataRows, variableKey, {
          values: options,
        });
        const originalBase = originalData[0]?.base || totalBase;

        // Get filtered counts
        const filteredData = generateChartData(filteredRows, variableKey, {
          values: options,
        });

        filteredData.forEach((item) => {
          combinedData.push({
            name: item.name,
            value: item.value, // Count from filtered data
            percent: originalBase
              ? Number(((item.value / originalBase) * 100).toFixed(2))
              : 0, // Percent against original base
            base: originalBase, // Original base
            variable: key,
          });
        });

        combinedOptions.push(...options);
      });
    } else if (varType === "numeric") {
      variableKeys.forEach((key) => {
        const selectedVar = dataGroup[key];
        const variableKey = resolveVariableKey(selectedVar, key);

        const nums = filteredRows
          .map((r) => Number(r[variableKey]))
          .filter((n) => Number.isFinite(n));

        const base = nums.length;
        const mean = base ? nums.reduce((a, b) => a + b, 0) / base : 0;
        const variance = base
          ? nums.reduce((acc, n) => acc + Math.pow(n - mean, 2), 0) / base
          : 0;
        const stdDev = Math.sqrt(variance);

        combinedData.push({
          name: "Summary",
          count: nums.length,
          mean: Number(mean.toFixed(2)),
          stdDev: Number(stdDev.toFixed(2)),
          base,
          stackKey: key,
        });
      });
    }

    let qTitle = "No Question Title";

    for (const v of variableKeys) {
      const title = dataGroup?.[v]?.Qtitle?.trim();
      if (title && title !== "-") {
        qTitle = title;
        break;
      }
    }

    const newKPI = {
      id: uuidv4(),
      label: variableKeys.join(", "),
      variableName: variableKeys.length === 1 ? variableKeys[0] : variableKeys,
      type: "Bar",
      data: combinedData,
      options: combinedOptions,
      viewMode: "chart",
      isNumeric: varType === "numeric" && variableKeys.length === 1,
      qTitle: qTitle,
    };

    setTabs((prev) =>
      prev.map((t) =>
        t.id === selectedTabId ? { ...t, kpis: [...t.kpis, newKPI] } : t
      )
    );
  };

  const applyAllFilters = (rows, tab, globalFilters) => {
    if (!Array.isArray(rows)) return [];

    let filtered = [...rows];

    const normalize = (v) => {
      if (v === null || v === undefined) return "";
      return String(v).trim().toLowerCase();
    };

    /* ======================================================
       🔥 NEW: Resolve ACTUAL data column name
       (THIS IS THE FIX)
    ====================================================== */
    const getResolvedColumnName = (filter) => {
      const variableKey =
        filter.variable_name ||
        filter.variableName ||
        filter.varName ||
        filter.key;

      if (!variableKey) return null;

      const variableMeta = dataGroup?.[variableKey];
      return resolveVariableKey(variableMeta, variableKey);
    };

    /* ======================================================
       LOCAL (TAB) FILTERS
    ====================================================== */
    if (tab?.filters?.length) {
      tab.filters.forEach((f) => {
        const colName = getResolvedColumnName(f);
        if (!colName) return;

        const selected = normalize(f.selectedValue);
        if (!selected) return;

        filtered = filtered.filter((row) => {
          return normalize(row[colName]) === selected;
        });
      });
    }

    /* ======================================================
       GLOBAL FILTERS
    ====================================================== */
    if (globalFilters?.length) {
      globalFilters.forEach((f) => {
        const colName = getResolvedColumnName(f);
        if (!colName) return;

        const selected = normalize(f.selectedValue);
        if (!selected) return;

        filtered = filtered.filter((row) => {
          return normalize(row[colName]) === selected;
        });
      });
    }

    return filtered;
  };

  const handleAddFilter = () => {
    if (!selectedVariable || !selectedTab) {
      alert("Please select a variable and a tab first.");
      return;
    }

    if (
      selectedTab.filters.some(
        (f) => f.variable_name === selectedVariable.variableName
      )
    ) {
      alert("This variable is already added as a local filter.");
      return;
    }

    if (
      globalFilters.some(
        (f) => f.variable_name === selectedVariable.variableName
      )
    ) {
      alert("This variable is already added in Global Filters.");
      return;
    }

    const getLabel = (variable, key) => {
      const title =
        variable?.title || variable.Qtitle || variable.label || variable.Name;
      if (typeof title === "object" && title !== null) {
        if (Array.isArray(title)) return title[0]?.Name || title[0] || key;
        return title.Name || title.name || title?.title || key;
      }
      return typeof title === "string" ? title : key;
    };

    const label = getLabel(selectedVariable, selectedQuestionKey);
    const variableName = resolveVariableKey(
      selectedVariable,
      selectedQuestionKey
    );

    const options =
      selectedVariable.values?.map((v, i) => {
        let name, value;
        if (typeof v === "string") {
          name = v;
          value = v;
        } else if (typeof v === "object" && v !== null) {
          name = v?.title || v.label || v.Name || v.name || `Option ${i + 1}`;
          value =
            v.value ??
            v.Value ??
            v.index ??
            v.Id ??
            v.code ??
            v?.title ??
            v.Name ??
            `value_${i + 1}`;
        } else {
          name = `Option ${i + 1}`;
          value = `value_${i + 1}`;
        }

        return {
          Id: i + 1,
          Name: typeof name === "string" ? name : String(name),
          ParentId: v?.ParentId || [0],
          Selected: v?.Selected ?? false,
          SortOrder: i + 1,
          Value: typeof value === "string" ? value : String(value),
          Visible: v?.Visible ?? false,
        };
      }) || [];

    const existingIds = selectedTab.filters
      .map((f) => Number(f.id))
      .filter((n) => !isNaN(n));
    const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;

    const newFilter = {
      id: nextId,
      global: 0,
      parent_id: null,
      label: label,
      variable_name: variableName,
      sort_order: selectedTab.filters.length + 1,
      Selected: null,
      multiple: 0,
      visible: true,
      hide_on_tab: false,
      positioning: 0,
      RoleId: null,
      options: options,
      selectedValue: "",
    };

    setTabs((prev) =>
      prev.map((tab) =>
        tab.id === selectedTabId
          ? { ...tab, filters: [...tab.filters, newFilter] }
          : tab
      )
    );
  };

  const handleAddGlobalFilter = () => {};

  const updateKPI = (tabId, kpiId, field, value) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.id === tabId
          ? {
              ...tab,
              kpis: tab.kpis.map((kpi) =>
                kpi.id === kpiId ? { ...kpi, [field]: value } : kpi
              ),
            }
          : tab
      )
    );
  };

  const updateFilter = (tabId, filterId, selectedValue) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.id === tabId
          ? {
              ...tab,
              filters: tab.filters.map((f) =>
                f.id === filterId ? { ...f, selectedValue } : f
              ),
            }
          : tab
      )
    );
  };

  const updateGlobalFilter = (id, selectedValue) => {
    setGlobalFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, selectedValue } : f))
    );
  };

  const addTab = () => {
    const name = prompt("Enter new tab name:");
    if (!name) return;
    const newTab = { id: uuidv4(), title: name, filters: [], kpis: [] };
    setTabs((prev) => [...prev, newTab]);
    setSelectedTabId(newTab.id);
  };

  const renameTab = (tabId) => {
    const newName = prompt("Enter new tab name:");
    if (!newName) return;
    setTabs((prev) =>
      prev.map((t) => (t.id === tabId ? { ...t, title: newName } : t))
    );
  };

  const saveDashboardToJson = () => {
    const payload = {
      logo: logoPath,
      tabs,
      globalFilters: globalFilters.map((f) => ({
        ...f,
        value: f.selectedValue || f.value,
      })),
      selectedTabId,
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    saveAs(blob, "dashboard.json");
  };

  const removeKPI = (tabId, kpiId) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.id === tabId
          ? { ...tab, kpis: tab.kpis.filter((kpi) => kpi.id !== kpiId) }
          : tab
      )
    );
  };

  const removeFilter = (tabId, filterId) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.id === tabId
          ? { ...tab, filters: tab.filters.filter((f) => f.id !== filterId) }
          : tab
      )
    );
  };

  const removeGlobalFilter = (filterId) => {
    setGlobalFilters((prev) => prev.filter((f) => f.id !== filterId));
  };

  const toggleKPIViewMode = (tabId, kpiId) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.id !== tabId
          ? tab
          : {
              ...tab,
              kpis: tab.kpis.map((k) =>
                k.id === kpiId
                  ? {
                      ...k,
                      viewMode: k.viewMode === "chart" ? "table" : "chart",
                    }
                  : k
              ),
            }
      )
    );
  };

  const fetchFrequency = async (selectedVariable) => {
    const response = await fetch(`${config.API_BASE_URL}/Dashboardfrequency`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        selectedVariable: selectedVariable,
        projectId: projectId,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP ${response.status}: ${text}`);
    }

    const data = await response.json();
    setDataDashboard(data);
  };

  return (
    <div className="dashboard-wrapper">
      <Sidebar
        dataGroup={dataGroup}
        selectedVariable={selectedVariable}
        selectedQuestionKey={selectedQuestionKey}
        setSelectedQuestionKey={setSelectedQuestionKey}
        handleAddKPI={handleAddKPI}
        handleAddFilter={handleAddFilter}
        handleAddGlobalFilter={handleAddGlobalFilter}
        // handleGenerateCrosstab={handleGenerateCrosstab}
        globalFilters={globalFilters}
        selectedTab={selectedTab}
        showCrosstabModal={showCrosstabModal}
        setShowCrosstabModal={setShowCrosstabModal}
        crosstabDisplayData={crosstabDisplayData}
        setCrosstabDisplayData={setCrosstabDisplayData}
      />

      <div className="main-content" style={{ overflow: "hidden" }}>
        {dataDashboard && (
          <MultiResponse
            varProperties={dataDashboard}
            displaySettings={{ showFrequency: true, showPercent: true }}
          />
        )}
        <GlobalFilters
          globalFilters={globalFilters}
          setGlobalFilters={setGlobalFilters}
          updateGlobalFilter={updateGlobalFilter}
          variables={dataGroup}
          tabs={tabs}
          removeGlobalFilter={removeGlobalFilter}
        />

        <TabNavigation
          tabs={tabs}
          selectedTabId={selectedTabId}
          setSelectedTabId={setSelectedTabId}
          addTab={addTab}
          renameTab={renameTab}
        />

        <div
          style={{
            margin: "10px 0",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <label style={{ fontWeight: "bold" }}>Logo Path:</label>
          <input
            type="text"
            placeholder="Enter logo URL or path..."
            value={logoPath}
            onChange={(e) => setLogoPath(e.target.value)}
            style={{ width: "300px" }}
          />
        </div>

        <button className="download-json-btn" onClick={saveDashboardToJson}>
          Download Dashboard JSON
        </button>
        <div className="tab-content-area">
          <TabContent
            selectedTab={selectedTab}
            filteredRows={applyAllFilters(
              originalDataRows,
              selectedTab,
              globalFilters
            )}
            updateKPI={updateKPI}
            updateFilter={updateFilter}
            globalFilters={globalFilters}
            removeKPI={removeKPI}
            removeFilter={removeFilter}
            toggleKPIViewMode={toggleKPIViewMode}
            crosstabDisplayData={crosstabDisplayData}
            setCrosstabDisplayData={setCrosstabDisplayData}
          />
        </div>
      </div>
      {/* <button onClick={() => fetchFrequency(selectedVariable)}>Get FREQ</button> */}
      {/* {JSON.stringify(dataDashboard)} */}
    </div>
  );
};

export default DashboardBuilder;
